import React, { useState, useEffect } from 'react';
import { Image, View, StyleSheet } from 'react-native';
import { Color, FontSize, CommonStyles } from '../../styles';
import Images from '../../constants/Images';
import Icon from '../Icon';
import Card from './Card';
import { formatCurrency } from '../../utils/currency.utils';
import {
  cardStyle,
  XSmallText,
  ShrinkView,
  CardLeftTextDetail,
  getStringRating,
  PerUnitPrice,
} from './card.utils';
import TextWithIcon from '../TextWithIcon';
import RowView from '../RowView';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import i18n, { useComponentTKey, i18nNS } from '../../i18n';

const transformHotelDataToCard = (item = {}, currency, currencyCulture) => {
  const policyReasonCode = item.reasonCode?.toLowerCase();
  const value = formatCurrency(item.price, currency, currencyCulture);
  const textUpStrike = item.AverageBaseCost !== 0 && item.AverageBaseCost !== item.price;
  return {
    title: item.HotelName,
    subtitle: Math.floor(item.distance * 10) / 10 + 'mi - ' + item.Address1,
    value,
    textUpStrike,
    textUpValue: textUpStrike
      ? formatCurrency(item.AverageBaseCost, currency, currencyCulture)
      : i18n.t('resultCards:HotelCard.From'),
    policy: policyReasonCode,
    image: item.HotelImage ? { uri: item.HotelImage } : Images.defaultImage,
    realRating: Images.rating[item.RealRating || getStringRating(item.StarRating)],
    reviewRating: Images.tripadvisor[item.ReviewRating ? item.ReviewRating : 0],
  };
};

const HotelDetails = ({ item = {}, currency, currencyCulture }) => {
  const { tKey } = useComponentTKey('HotelCard');
  const { t } = useTranslation(i18nNS.resultCards);

  return (
    <View style={cardStyle.composedRow}>
      <ShrinkView>
        <RowView>
          <CardLeftTextDetail />
          <ShrinkView>
            <XSmallText>{item.Address1}</XSmallText>
          </ShrinkView>
        </RowView>
        <RowView>
          <CardLeftTextDetail>{t(tKey('CheckIn'))}:</CardLeftTextDetail>
          <XSmallText>{moment(item.CheckInDate).format('LL')}</XSmallText>
        </RowView>
        <RowView>
          <CardLeftTextDetail>{t(tKey('CheckOut'))}:</CardLeftTextDetail>
          <ShrinkView>
            <XSmallText>{moment(item.CheckOutDate).format('LL')}</XSmallText>
            {item.IsCarCorporateFareAvailable && (
              <TextWithIcon iconName="corporate" iconColor={Color.corporate}>
                Corporate Rate
              </TextWithIcon>
            )}
            <TextWithIcon
              iconName="circleCancel"
              iconColor={
                item?.HotelTravelComponentDetailSegments[0]?.IsNonRefundable
                  ? Color.required
                  : Color.primary
              }
            >
              {item?.HotelTravelComponentDetailSegments.map((hotel) => {
                return hotel.IsNonRefundable
                  ? t(tKey('Non-refundable'))
                  : t(tKey('FreeCancelation'));
              })}
            </TextWithIcon>
          </ShrinkView>
        </RowView>
      </ShrinkView>
      <View style={CommonStyles.ml2}>
        <PerUnitPrice
          label={t(tKey('PerNight'))}
          value={formatCurrency(item.RateCost, currency, currencyCulture)}
        />
      </View>
    </View>
  );
};

const HotelCard = ({ item, currency, currencyCulture, ...rest }) => {
  useTranslation(i18nNS.resultCards);

  const [data, setData] = useState(null);
  useEffect(() => {
    if (Object.keys(item).length > 0) {
      setData(transformHotelDataToCard(item, currency, currencyCulture));
    }
  }, [item]);

  return data ? (
    <Card
      title={data.title}
      disabled={item.IsSuppressHotel}
      subtitle={data.subtitle}
      value={data.value}
      textUpValue={data.textUpValue}
      textDownValue=" "
      textUpStrike={data.textUpStrike}
      policy={data.policy}
      image={data.image}
      hideIcon
      childrenTitle={
        <View style={[cardStyle.textRow, CommonStyles.mt2]}>
          <Image style={[cardStyle.rating, CommonStyles.mr2]} source={data.realRating} />
          <Image style={cardStyle.rating} source={data.reviewRating} />
        </View>
      }
      detail={<HotelDetails item={item} currency={currency} currencyCulture={currencyCulture} />}
      {...rest}
    >
      <RowView>
        {item.IsCorporateRate && (
          <Icon style={[styles.iconAlone, CommonStyles.my1]} name="corporate" />
        )}
        {item.HasGovRate && (
          <Icon style={[styles.iconAlone, CommonStyles.my1]} name="governmentPolicy" />
        )}
      </RowView>
    </Card>
  ) : (
    <></>
  );
};

const styles = StyleSheet.create({
  iconAlone: {
    fontSize: FontSize.small,
    color: Color.corporate,
  },
});

export default HotelCard;
